import { useQueries } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ORGANIZATION_TAB } from '@/types';
import { QUERY_KEYS } from '@/constants/queryKeys';
import { caminoService } from '@/services/camino.service';
import { myParishService } from '@/services/my-parish.service';
import { messagingService } from '@/services/messaging.service';
import { formService } from '@/services/form.service';
import { ministrySchedulerService } from '@/services/ministry-scheduler.service';

type TabStatus = {
  [key in ORGANIZATION_TAB]: boolean;
};

const defaultTabStatus: TabStatus = {
  [ORGANIZATION_TAB.ORG_ADMINS]: true,
  [ORGANIZATION_TAB.GROUP_ADMINS]: true,
  [ORGANIZATION_TAB.MPA]: false,
  [ORGANIZATION_TAB.MINISTRY_SCHEDULER]: false,
  [ORGANIZATION_TAB.MASS_INTENTIONS]: false,
  [ORGANIZATION_TAB.ADORATION]: false,
  [ORGANIZATION_TAB.FACILITY_SCHED]: false,
  [ORGANIZATION_TAB.CAMINO]: false,
  [ORGANIZATION_TAB.DIOCESE_INFO]: false,
  [ORGANIZATION_TAB.MESSAGING]: false,
  [ORGANIZATION_TAB.DIRECT_MESSAGES]: true,
  [ORGANIZATION_TAB.FORMS]: false,
  [ORGANIZATION_TAB.EVA_SYNC]: true,
};

type UseTabStatusReturn = {
  tabStatus: TabStatus;
  isLoading: boolean;
  error: string | null;
  refreshTabStatus: () => Promise<void>;
};

export const useTabStatus = (): UseTabStatusReturn => {
  const { id: churchId } = useParams<{ id: string }>();

  const results = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.CAMINO, churchId],
        queryFn: () => caminoService.getCamino(churchId ?? ''),
        enabled: !!churchId,
      },
      {
        queryKey: [QUERY_KEYS.MY_PARISH, churchId],
        queryFn: () => myParishService.getMyParish(churchId ?? ''),
        enabled: !!churchId,
      },
      {
        queryKey: [QUERY_KEYS.MESSAGING, churchId],
        queryFn: () => messagingService.getMessaging(churchId ?? ''),
        enabled: !!churchId,
      },
      {
        queryKey: [QUERY_KEYS.FORM, churchId],
        queryFn: () => formService.getForm(churchId ?? ''),
        enabled: !!churchId,
      },
      {
        queryKey: [QUERY_KEYS.MINISTRY_SCHEDULER, churchId],
        queryFn: () => ministrySchedulerService.getMinistryScheduler(churchId ?? ''),
        enabled: !!churchId,
      },
    ],
  });

  const [caminoResult, myParishResult, messagingResult, formsResult, ministrySchedulerResult] = results;
  const isLoading = results.some((r) => r.isLoading);
  const hasError = results.some((r) => r.isError);

  const tabStatus: TabStatus = {
    ...defaultTabStatus,
    [ORGANIZATION_TAB.CAMINO]: !!caminoResult.data?.isActive,
    [ORGANIZATION_TAB.MPA]: !!myParishResult.data?.isActive,
    [ORGANIZATION_TAB.MESSAGING]: !!messagingResult.data?.isActive,
    [ORGANIZATION_TAB.FORMS]: !!formsResult.data?.isActive,
    [ORGANIZATION_TAB.MINISTRY_SCHEDULER]: !!ministrySchedulerResult.data?.isActive,
  };

  const refreshTabStatus = async () => {
    await Promise.all(results.map((r) => r.refetch()));
  };

  return {
    tabStatus,
    isLoading,
    error: hasError ? 'Failed to load tab status' : null,
    refreshTabStatus,
  };
};
